import React, { useState, useRef } from 'react';
import { Creatable } from 'react-select';
import { Button, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row } from 'reactstrap';
import { Colxx } from './CustomBootstrap';
import CustomSelectInput from './CustomSelectInput';
import ImageUploader from 'react-images-upload';
import Resizer from 'react-image-file-resizer';
import { storage } from '../../helpers/Firebase';

const CreatableFinish = ({
    selectedFinish = [],
    availableFinish = [],
    onChange,
    onCreateFinish,
    disabled = false,
    placeholder = "Select finish...",
    className = "react-select",
    collection = 'services',
    parentId = null
}) => {
    const [newFinishModal, setNewFinishModal] = useState(false);
    const [newFinish, setNewFinish] = useState({ name: '', name_ar: '', key: '', edit: false, image: null });
    const [finishPicture, setFinishPicture] = useState(null);
    const isProcessingImage = useRef(false);

    // Group styles for react-select
    const groupStyles = {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
    };

    const groupBadgeStyles = {
        backgroundColor: '#EBECF0',
        borderRadius: '2em',
        color: '#172B4D',
        display: 'inline-block',
        fontSize: 12,
        fontWeight: 'normal',
        lineHeight: '1',
        minWidth: 1,
        padding: '0.16666666666667em 0.5em',
        textAlign: 'center',
    };

    const formatGroupLabel = (data) => (
        <div style={groupStyles}>
            <span>{data.label}</span>
            <span style={groupBadgeStyles}>{data.options?.length || 0}</span>
        </div>
    );

    // Handle selection change
    const handleChange = (selectedOption) => {
        if (selectedOption) {
            const newSelectedFinish = [...selectedFinish];
            newSelectedFinish.push({ 
                name: selectedOption.name, 
                name_ar: selectedOption.name_ar, 
                key: selectedOption.key 
            });
            onChange && onChange(newSelectedFinish);
        }
    };

    // Handle image upload for finish
    const onDropFinishImage = (picture) => {
        // Check if picture array exists and has items
        if (picture && Array.isArray(picture) && picture.length > 0) {
            let lastPic = picture[picture.length - 1];

            // Only process if it's actually a file
            if (lastPic instanceof File || lastPic instanceof Blob) {
                isProcessingImage.current = true;
                Resizer.imageFileResizer(
                    lastPic,
                    1080,
                    1080,
                    'png',
                    100,
                    0,
                    uri600 => {
                        const newPicture = {
                            name: '600',
                            data: uri600
                        };
                        setFinishPicture([newPicture]);
                        // Store preview URL for immediate display
                        const previewUrl = URL.createObjectURL(lastPic);
                        // Use functional update to avoid stale closure
                        setNewFinish(prevState => ({ ...prevState, image: previewUrl }));
                        isProcessingImage.current = false;
                    },
                    'blob'
                );
            }
        } else {
            // Empty array - user removed the image
            // Only clear if we're not currently processing an image
            if (!isProcessingImage.current) {
                setNewFinish(prevState => {
                    if (prevState.image) {
                        // Revoke the object URL to free memory
                        if (prevState.image.startsWith('blob:')) {
                            URL.revokeObjectURL(prevState.image);
                        }
                        return { ...prevState, image: null };
                    }
                    return prevState;
                });
                setFinishPicture(null);
            }
        }
    };

    // Upload image and get download URL
    const uploadImageAndGetUrl = async (pictureData, finishKey) => {
        return new Promise((resolve, reject) => {
            const metadata = {
                cacheControl: 'public,max-age=300',
                contentType: 'image/png'
            };

            // Create a unique path for the finish image
            const imagePath = `finish/${finishKey}_${Date.now()}.png`;
            const storageRef = storage().ref().child(imagePath);
            const uploadTask = storageRef.put(pictureData.data, metadata);

            uploadTask.on(
                'state_changed',
                (snapshot) => {
                    // Progress tracking (optional)
                },
                (error) => {
                    console.error('Error uploading image:', error);
                    reject(error);
                },
                () => {
                    // Upload completed successfully
                    uploadTask.snapshot.ref.getDownloadURL().then((downloadURL) => {
                        resolve(downloadURL);
                    }).catch((error) => {
                        console.error('Error getting download URL:', error);
                        reject(error);
                    });
                }
            );
        });
    };

    // Generate ID from name (slug-like format)
    const generateIdFromName = (name) => {
        if (!name) return '';
        return name
            .toLowerCase()
            .trim()
            .replace(/[^a-z0-9]+/g, '_')  // Replace non-alphanumeric with underscore
            .replace(/^_+|_+$/g, '')       // Remove leading/trailing underscores
            .substring(0, 50);             // Limit length
    };

    // Handle creating new finish
    const handleCreateOption = (inputValue) => {
        const autoGeneratedKey = generateIdFromName(inputValue);
        setNewFinish({ name: inputValue, name_ar: '', key: autoGeneratedKey, edit: false, image: null });
        setNewFinishModal(true);
    };

    // Save new finish
    const saveNewFinish = async () => {
        if (!newFinish.name) return;

        // Auto-generate key from name if missing (only for new items, not on update)
        let finalKey = newFinish.key;
        if (!finalKey && !newFinish.edit) {
            finalKey = generateIdFromName(newFinish.name);
        }

        let imageUrl = newFinish.image || null;

        // Upload image if available and get the download URL
        if (finishPicture && finishPicture.length > 0) {
            try {
                imageUrl = await uploadImageAndGetUrl(finishPicture[0], finalKey);
            } catch (error) {
                console.error('Failed to upload image:', error);
                // Continue without image if upload fails
            }
        }

        const newFinishData = {
            name: newFinish.name,
            name_ar: newFinish.name_ar,
            key: finalKey,
            image: imageUrl
        };

        let newData = [...selectedFinish];
        
        if (newFinish.edit) {
            // Edit existing - preserve existing image if not changed
            newData = newData.map(item => {
                if (item.key === newFinish.key) {
                    return {
                        ...newFinishData,
                        // Preserve existing image if new image wasn't uploaded
                        image: imageUrl || item.image || null
                    };
                }
                return item;
            });
        } else {
            // Add new
            newData.push(newFinishData);
        }

        onChange && onChange(newData);
        onCreateFinish && onCreateFinish(newFinishData);
        
        setNewFinishModal(false);
        setFinishPicture(null);
        setNewFinish({ name: '', name_ar: '', key: '', edit: false, image: null });
    };

    // Remove finish
    const removeFinish = (finishKey) => {
        const newData = selectedFinish.filter(item => item.key !== finishKey);
        onChange && onChange(newData);
    };

    // Edit finish
    const editFinish = (finish) => {
        setNewFinish({
            name: finish.name,
            name_ar: finish.name_ar,
            key: finish.key,
            edit: true,
            image: finish.image || null
        });
        setFinishPicture(null);
        setNewFinishModal(true);
    };

    // Get available options (filtered to exclude already selected)
    const getAvailableOptions = () => {
        const selectedKeys = selectedFinish.map(finish => finish.key);
        return availableFinish.filter(finish => !selectedKeys.includes(finish.key));
    };

    return (
        <div className="creatable-finish-wrapper">
            {/* React Select Component */}
            <Creatable
                formatGroupLabel={formatGroupLabel}
                components={{ Input: CustomSelectInput }}
                className={className}
                options={getAvailableOptions()}
                value=""
                onChange={handleChange}
                onCreateOption={handleCreateOption}
                isDisabled={disabled}
                placeholder={placeholder}
                isClearable
            />

            {/* Selected Finish Display */}
            {selectedFinish.length > 0 && (
                <div className="mb-2 mt-2" style={{
                    minHeight: 'auto',
                    borderRadius: '3px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    flexWrap: 'wrap',
                }}>
                    {selectedFinish.map((finish, index) => (
                        <div key={index} style={{
                            margin: '5px',
                            padding: '8px 12px',
                            border: '1px solid #d7d7d7',
                            borderRadius: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px'
                        }}>
                            {finish.image && (
                                <img 
                                    src={finish.image} 
                                    alt={finish.name}
                                    style={{
                                        width: '24px',
                                        height: '24px',
                                        objectFit: 'cover',
                                        borderRadius: '4px'
                                    }}
                                />
                            )}
                            <div style={{ marginRight: '4px' }}>{finish.name}</div>
                            <i 
                                style={{ cursor: 'pointer', marginRight: '5px' }} 
                                className="simple-icon-close" 
                                onClick={() => removeFinish(finish.key)}
                                title="Remove finish"
                            />
                            <i 
                                style={{ cursor: 'pointer' }} 
                                className="simple-icon-pencil" 
                                onClick={() => editFinish(finish)}
                                title="Edit finish"
                            />
                        </div>
                    ))}
                </div>
            )}

            {/* New/Edit Finish Modal */}
            <Modal backdrop="static" isOpen={newFinishModal} toggle={() => {
                setNewFinishModal(false);
                setFinishPicture(null);
                setNewFinish({ name: '', name_ar: '', key: '', edit: false, image: null });
                isProcessingImage.current = false;
            }}>
                <ModalHeader toggle={() => {
                    setNewFinishModal(false);
                    setFinishPicture(null);
                    setNewFinish({ name: '', name_ar: '', key: '', edit: false, image: null });
                    isProcessingImage.current = false;
                }}>
                    {newFinish.edit ? 'Edit Finish' : 'Create New Finish'}
                </ModalHeader>
                <ModalBody>
                    <Row>
                        {[
                            { key: 'name', title: 'Finish Name', width: '12', type: 'text' },
                            { key: 'name_ar', title: 'Finish Arabic Name', width: '12', type: 'text' },
                        ].map((item, index) => (
                            <Colxx key={index} xxs="12" md={item.width} className="mb-4">
                                <Label for={item.key}>{item.title}</Label>
                                <Input
                                    type={item.type}
                                    id={item.key}
                                    placeholder={"e.g. " + item.title}
                                    value={newFinish[item.key]}
                                    className="customInput"
                                    disabled={item.disabled}
                                    onChange={(e) => {
                                        // Auto-generate key from name only when creating (not editing)
                                        const updatedFinish = { ...newFinish, [item.key]: e.target.value };
                                        if (!newFinish.edit && item.key === 'name') {
                                            updatedFinish.key = generateIdFromName(e.target.value);
                                        }
                                        setNewFinish(updatedFinish);
                                    }}
                                />
                            </Colxx>
                        ))}
                        {!newFinish.edit && newFinish.key && (
                            <Colxx xxs="12" md="12" className="mb-4">
                                <Label>Finish ID</Label>
                                <Input
                                    type="text"
                                    value={newFinish.key}
                                    className="customInput"
                                    disabled={true}
                                />
                                <small className="text-muted" style={{ display: 'block', marginTop: '5px' }}>
                                    Auto-generated from name
                                </small>
                            </Colxx>
                        )}
                        <Colxx xxs="12" md="12" className="mb-4">
                            <Label>Finish Image</Label>
                            <ImageUploader
                                className={'w-100'}
                                withIcon={true}
                                label={'Max file size: 15mb, accepted: jpg|gif|png'}
                                buttonText='Choose image'
                                onChange={onDropFinishImage}
                                imgExtension={['.jpg', '.gif', '.png', '.jpeg']}
                                maxFileSize={15728640}
                                withPreview={true}
                                singleImage={true}
                                defaultImages={newFinish?.image ? [newFinish.image] : []}
                            />
                        </Colxx>
                    </Row>
                </ModalBody>
                <ModalFooter>
                    <Button color="danger" onClick={() => {
                        setNewFinishModal(false);
                        setFinishPicture(null);
                        setNewFinish({ name: '', name_ar: '', key: '', edit: false, image: null });
                        isProcessingImage.current = false;
                    }}>
                        Cancel
                    </Button>
                    <Button
                        color="primary"
                        disabled={!newFinish.name}
                        onClick={saveNewFinish}
                    >
                        {newFinish.edit ? 'Update' : 'Add'}
                    </Button>
                </ModalFooter>
            </Modal>
        </div>
    );
};

export default CreatableFinish;

